import apiClient from './api.service';

export const epcEstimationService = {
  async runtime() {
    const res = await apiClient.get('/epc/estimations/runtime');
    return (res as any)?.data ?? res;
  },
  async list(tenderId?: string) {
    const res = await apiClient.get('/epc/estimations', {
      params: tenderId ? { tender_id: tenderId } : undefined,
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async create(body: {
    tenderId: string;
    title?: string;
    currency?: string;
    departmentId?: string;
  }) {
    const res = await apiClient.post('/epc/estimations', body);
    return (res as any)?.data ?? res;
  },
  async getWorkspace(id: string) {
    const res = await apiClient.get(`/epc/estimations/${id}`);
    return (res as any)?.data ?? res;
  },
  /** Resolve revision → tender/estimate for approval deep links. */
  async getRevisionContext(revisionId: string) {
    const res = await apiClient.get(
      `/epc/estimations/revision-context/${encodeURIComponent(revisionId)}`
    );
    return (res as any)?.data ?? res;
  },
  async createRevision(id: string, revisionReason?: string) {
    const res = await apiClient.post(`/epc/estimations/${id}/revisions`, { revisionReason });
    return (res as any)?.data ?? res;
  },
  async listWbs(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/wbs`, {
      params: { revision_id: revisionId },
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async listWbsLayers(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/wbs-layers`);
    return ((res as any)?.data ?? res) as any[];
  },
  async downloadWbsDictionary(estimateId: string, revisionId: string): Promise<Blob> {
    const api = (await import('./api.service')).default;
    const res = await api.getClient().get(`/epc/estimations/${estimateId}/wbs/dictionary`, {
      params: { revision_id: revisionId },
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async guidedSetupWbs(
    estimateId: string,
    body: {
      revisionId: string;
      nodes: Array<{
        code: string;
        name: string;
        wbsLayerCode: string;
        parentCode?: string;
        description?: string;
        shortName?: string;
        referenceScopeCode?: string;
        sortOrder?: number;
      }>;
    }
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/wbs/guided-setup`, body);
    return (res as any)?.data ?? res;
  },
  async createWbs(
    estimateId: string,
    body: {
      revisionId: string;
      code: string;
      name: string;
      parentId?: string;
      wbsLayerId?: string;
      wbsLayerCode?: string;
      description?: string;
      shortName?: string;
      referenceScopeCode?: string;
      sortOrder?: number;
    }
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/wbs`, body);
    return (res as any)?.data ?? res;
  },
  async patchWbs(
    estimateId: string,
    wbsId: string,
    body: {
      revisionId: string;
      code?: string;
      name?: string;
      parentId?: string | null;
      wbsLayerId?: string | null;
      wbsLayerCode?: string;
      description?: string | null;
      shortName?: string | null;
      referenceScopeCode?: string | null;
    }
  ) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/wbs/${wbsId}`, body);
    return (res as any)?.data ?? res;
  },
  async deleteWbs(estimateId: string, wbsId: string, revisionId: string) {
    const res = await apiClient.delete(`/epc/estimations/${estimateId}/wbs/${wbsId}`, {
      data: { revisionId },
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async downloadWbsImportTemplate(estimateId: string): Promise<Blob> {
    const api = (await import('./api.service')).default;
    const res = await api.getClient().get(`/epc/estimations/${estimateId}/wbs/import-template`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async importWbs(
    estimateId: string,
    revisionId: string,
    file: File,
    format: 'auto' | 'est3' | 'primavera' | 'msproject' = 'auto'
  ) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('revisionId', revisionId);
    if (format !== 'auto') fd.append('format', format);
    const { postExcelImportWithRetry } = await import('@/utils/excelImportUpload');
    const api = (await import('./api.service')).default;
    const res = await postExcelImportWithRetry(
      api.getClient(),
      `/epc/estimations/${estimateId}/wbs/import`,
      fd
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async copyWbsFromProject(
    estimateId: string,
    body: { revisionId: string; projectId: string; structureId?: string }
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/wbs/copy-from-project`, body);
    return (res as any)?.data ?? res;
  },
  async listCbs(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/cbs`);
    return ((res as any)?.data ?? res) as any[];
  },
  async readiness(id: string) {
    const res = await apiClient.get(`/epc/estimations/${id}/readiness`);
    return (res as any)?.data ?? res;
  },
  async diagnostics(id: string) {
    const res = await apiClient.get(`/epc/estimations/${id}/diagnostics`);
    return (res as any)?.data ?? res;
  },
  async submitForApproval(id: string, revisionId: string) {
    const res = await apiClient.post(`/epc/estimations/${id}/submit-for-approval`, { revisionId });
    return (res as any)?.data ?? res;
  },

  async listClientBoq(
    estimateId: string,
    revisionId: string,
    opts?: { limit?: number; offset?: number }
  ) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/client-boq`, {
      params: {
        revision_id: revisionId,
        ...(opts?.limit != null ? { limit: opts.limit, offset: opts.offset ?? 0 } : {}),
      },
    });
    const data = (res as any)?.data ?? res;
    if (Array.isArray(data)) return data as any[];
    return (data?.items ?? data) as any[];
  },
  async createClientBoq(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-boq`, body);
    return (res as any)?.data ?? res;
  },
  async importClientBoq(
    estimateId: string,
    revisionId: string,
    file: File,
    mapping?: Record<string, unknown>
  ) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('revisionId', revisionId);
    if (mapping) fd.append('mapping', JSON.stringify(mapping));
    const { postExcelImportWithRetry } = await import('@/utils/excelImportUpload');
    const api = (await import('./api.service')).default;
    const res = await postExcelImportWithRetry(
      api.getClient(),
      `/epc/estimations/${estimateId}/client-boq/import`,
      fd,
      { timeout: 600000 }
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async previewClientBoqImport(
    estimateId: string,
    revisionId: string,
    file: File,
    mapping?: Record<string, unknown>
  ) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('revisionId', revisionId);
    if (mapping) fd.append('mapping', JSON.stringify(mapping));
    const { postExcelImportWithRetry } = await import('@/utils/excelImportUpload');
    const api = (await import('./api.service')).default;
    const res = await postExcelImportWithRetry(
      api.getClient(),
      `/epc/estimations/${estimateId}/client-boq/import/preview`,
      fd
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async downloadClientBoqImportTemplate(estimateId: string): Promise<Blob> {
    const api = (await import('./api.service')).default;
    const res = await api.getClient().get(`/epc/estimations/${estimateId}/client-boq/import-template`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  async analyzeClientBoqPdf(estimateId: string, revisionId: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('revisionId', revisionId);
    const { postExcelImportWithRetry } = await import('@/utils/excelImportUpload');
    const api = (await import('./api.service')).default;
    const res = await postExcelImportWithRetry(
      api.getClient(),
      `/epc/estimations/${estimateId}/client-boq/import/pdf/analyze`,
      fd,
      { timeout: 600000 }
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async getClientBoqPdfAnalyzeJob(estimateId: string, jobId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/client-boq/import/pdf/jobs/${jobId}`
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async previewClientBoqPdfImport(
    estimateId: string,
    body: { revisionId: string; jobId: string; patches?: unknown[] }
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/import/pdf/preview`,
      body
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async importClientBoqFromPdf(
    estimateId: string,
    body: {
      revisionId: string;
      jobId?: string;
      patches?: unknown[];
      rows?: unknown[];
      fileName?: string;
      batchId?: string;
      documentId?: string;
      importMode?: 'append' | 'replace';
    }
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-boq/import/pdf`, body, {
      timeout: 600000,
    } as any);
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async mutateClientBoqPdfRows(
    estimateId: string,
    body: { jobId: string; type: 'merge' | 'split'; row: number; atChar?: number }
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/import/pdf/mutate`,
      body
    );
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async clearClientBoq(estimateId: string, revisionId: string) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-boq/clear`, {
      revisionId,
    });
    return (res as any)?.data?.data ?? (res as any)?.data ?? res;
  },
  async listCostLines(
    estimateId: string,
    revisionId: string,
    opts?: { clientBoqLineId?: string; limit?: number; offset?: number }
  ) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/cost-lines`, {
      params: {
        revision_id: revisionId,
        ...(opts?.clientBoqLineId ? { client_boq_line_id: opts.clientBoqLineId } : {}),
        ...(opts?.limit != null ? { limit: opts.limit, offset: opts.offset ?? 0 } : {}),
      },
    });
    const data = (res as any)?.data ?? res;
    if (Array.isArray(data)) return data as any[];
    return (data?.items ?? data) as any[];
  },
  async createCostLine(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/cost-lines`, body);
    return (res as any)?.data ?? res;
  },
  async bulkAssignCostLines(
    estimateId: string,
    revisionId: string,
    body: { lineIds: string[]; wbsNodeId?: string; cbsCodeId?: string; clientBoqLineId?: string }
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/cost-lines/bulk-assign`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async patchCostLine(
    estimateId: string,
    lineId: string,
    revisionId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/cost-lines/${lineId}`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async patchClientBoq(
    estimateId: string,
    lineId: string,
    revisionId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/client-boq/${lineId}`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  /** NextGen P1 — batch BOQ edits with optional updated_at concurrency. */
  async bulkPatchClientBoq(
    estimateId: string,
    revisionId: string,
    patches: Array<{
      lineId: string;
      expectedUpdatedAt?: string | null;
      quantity?: number;
      unitRate?: number;
      description?: string;
      notes?: string;
      uom?: string;
    }>
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-boq/bulk-patch`, {
      revisionId,
      patches,
    });
    return (res as any)?.data ?? res;
  },
  async boqCostCoverage(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/boq-cost-coverage`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async getClientBoqLineDetail(estimateId: string, revisionId: string, lineId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/client-boq/${lineId}/detail`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },
  async listMaterialComponents(estimateId: string, lineId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/material-components`
    );
    return ((res as any)?.data ?? res) as any[];
  },
  async createMaterialComponent(
    estimateId: string,
    lineId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/material-components`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async deleteMaterialComponent(
    estimateId: string,
    lineId: string,
    componentId: string,
    revisionId: string
  ) {
    const res = await apiClient.delete(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/material-components/${componentId}`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },
  async costExplorer(
    estimateId: string,
    revisionId: string,
    view: 'wbs' | 'cbs' | 'client_boq' = 'wbs'
  ) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/cost-explorer`, {
      params: { revision_id: revisionId, view },
    });
    return (res as any)?.data ?? res;
  },

  async listLabourClasses(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/labour-classes`);
    return ((res as any)?.data ?? res) as any[];
  },
  async listEquipmentClasses(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/equipment-classes`);
    return ((res as any)?.data ?? res) as any[];
  },
  async listCrewTemplates(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/crew-templates`);
    return ((res as any)?.data ?? res) as any[];
  },
  async createCrewTemplate(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/crew-templates`, body);
    return (res as any)?.data ?? res;
  },
  async listProductivityNorms(estimateId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/productivity-norms`);
    return ((res as any)?.data ?? res) as any[];
  },
  async createProductivityNorm(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/productivity-norms`, body);
    return (res as any)?.data ?? res;
  },
  async upsertLabourRate(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/labour-rates`, body);
    return (res as any)?.data ?? res;
  },
  async upsertEquipmentRate(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/equipment-rates`, body);
    return (res as any)?.data ?? res;
  },
  async listLabourComponents(estimateId: string, lineId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/labour-components`
    );
    return ((res as any)?.data ?? res) as any[];
  },
  async createLabourComponent(estimateId: string, lineId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/labour-components`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async applyCrew(estimateId: string, lineId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/apply-crew`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async deleteLabourComponent(
    estimateId: string,
    lineId: string,
    componentId: string,
    revisionId: string
  ) {
    const res = await apiClient.delete(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/labour-components/${componentId}`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },
  async listEquipmentComponents(estimateId: string, lineId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/equipment-components`
    );
    return ((res as any)?.data ?? res) as any[];
  },
  async createEquipmentComponent(
    estimateId: string,
    lineId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/equipment-components`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async deleteEquipmentComponent(
    estimateId: string,
    lineId: string,
    componentId: string,
    revisionId: string
  ) {
    const res = await apiClient.delete(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/equipment-components/${componentId}`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },

  async listSubcontractPackages(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/subcontract-packages`, {
      params: { revision_id: revisionId },
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async createSubcontractPackage(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/subcontract-packages`, body);
    return (res as any)?.data ?? res;
  },
  async patchSubcontractPackage(
    estimateId: string,
    packageId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.patch(
      `/epc/estimations/${estimateId}/subcontract-packages/${packageId}`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async deleteSubcontractPackage(
    estimateId: string,
    packageId: string,
    revisionId: string
  ) {
    const res = await apiClient.delete(
      `/epc/estimations/${estimateId}/subcontract-packages/${packageId}`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },
  async listSubcontractQuoteRefs(estimateId: string, packageId: string) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/subcontract-packages/${packageId}/quote-refs`
    );
    return ((res as any)?.data ?? res) as any[];
  },
  async addSubcontractQuoteRef(
    estimateId: string,
    packageId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/subcontract-packages/${packageId}/quote-refs`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async selectSubcontractQuote(
    estimateId: string,
    packageId: string,
    quoteRefId: string,
    revisionId: string
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/subcontract-packages/${packageId}/quote-refs/${quoteRefId}/select`,
      { revisionId }
    );
    return (res as any)?.data ?? res;
  },
  async subcontractCoverage(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/subcontract-coverage`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async searchSupplierQuotes(estimateId: string, q?: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/supplier-quotes`, {
      params: q ? { q } : undefined,
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async searchBidAnalyses(estimateId: string, q?: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/bid-analyses`, {
      params: q ? { q } : undefined,
    });
    return ((res as any)?.data ?? res) as any[];
  },

  // Wave 5 — project cost
  async getDuration(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/duration`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async patchDuration(estimateId: string, revisionId: string, body: Record<string, unknown>) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/duration`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async listIndirectCosts(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/indirect-costs`, {
      params: { revision_id: revisionId },
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async createIndirectCost(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/indirect-costs`, body);
    return (res as any)?.data ?? res;
  },
  async patchIndirectCost(
    estimateId: string,
    lineId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.patch(
      `/epc/estimations/${estimateId}/indirect-costs/${encodeURIComponent(lineId)}`,
      body
    );
    return (res as any)?.data ?? res;
  },
  async deleteIndirectCost(estimateId: string, lineId: string, revisionId: string) {
    const res = await apiClient.delete(
      `/epc/estimations/${estimateId}/indirect-costs/${encodeURIComponent(lineId)}`,
      { params: { revision_id: revisionId } }
    );
    return (res as any)?.data ?? res;
  },
  async listRiskLinks(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/risk-links`, {
      params: { revision_id: revisionId },
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async createRiskLink(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/risk-links`, body);
    return (res as any)?.data ?? res;
  },
  async getContingency(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/contingency`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async patchContingency(estimateId: string, revisionId: string, body: Record<string, unknown>) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/contingency`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async listEscalationRules(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/escalation-rules`, {
      params: { revision_id: revisionId },
    });
    return ((res as any)?.data ?? res) as any[];
  },
  async createEscalationRule(estimateId: string, body: Record<string, unknown>) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/escalation-rules`, body);
    return (res as any)?.data ?? res;
  },
  async getFxAllowance(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/fx-allowance`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async patchFxAllowance(estimateId: string, revisionId: string, body: Record<string, unknown>) {
    const res = await apiClient.patch(`/epc/estimations/${estimateId}/fx-allowance`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async totalCostSummary(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/total-cost-summary`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async revisionCompare(estimateId: string, from: string, to: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/revision-compare`, {
      params: { from, to },
    });
    return (res as any)?.data ?? res;
  },

  // Wave 6 — commercial pricing
  async getPricing(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/pricing`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async putPricing(estimateId: string, revisionId: string, body: Record<string, unknown>) {
    const res = await apiClient.put(`/epc/estimations/${estimateId}/pricing`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async pricingSummary(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/pricing-summary`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async listClientPricing(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/client-pricing`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async allocateClientPricing(
    estimateId: string,
    revisionId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-pricing/allocate`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async previewAllocateClientPricing(
    estimateId: string,
    revisionId: string,
    body: Record<string, unknown> = {}
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/client-pricing/allocate`, {
      revisionId,
      preview: true,
      ...body,
    });
    return (res as any)?.data ?? res;
  },
  async pricingReconciliation(estimateId: string, revisionId: string) {
    const res = await apiClient.get(`/epc/estimations/${estimateId}/pricing-reconciliation`, {
      params: { revision_id: revisionId },
    });
    return (res as any)?.data ?? res;
  },
  async pricingSensitivity(
    estimateId: string,
    revisionId: string,
    body: Record<string, unknown>
  ) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/pricing/sensitivity`, {
      revisionId,
      ...body,
    });
    return (res as any)?.data ?? res;
  },

  async autoMapWbs(estimateId: string, revisionId: string, projectId: string) {
    const res = await apiClient.post(`/epc/estimations/${estimateId}/auto-map-wbs`, {
      revisionId,
      projectId,
    });
    return (res as any)?.data ?? res;
  },

  // ─── NextGen Phase 2 — costing methods / assembly / rate resolver ─────────

  async getCostingMethod(
    estimateId: string,
    revisionId: string,
    opts?: { boqLineId?: string }
  ) {
    const res = await apiClient.get(
      `/epc/estimations/${estimateId}/revisions/${revisionId}/costing-method`,
      {
        params: opts?.boqLineId
          ? { boqLineId: opts.boqLineId, boq_line_id: opts.boqLineId }
          : undefined,
      }
    );
    return (res as any)?.data ?? res;
  },

  async patchRevisionCostingMethod(
    estimateId: string,
    revisionId: string,
    body: { defaultCostingMethod: string }
  ) {
    const res = await apiClient.patch(
      `/epc/estimations/${estimateId}/revisions/${revisionId}/costing-method`,
      {
        defaultCostingMethod: body.defaultCostingMethod,
        method: body.defaultCostingMethod,
      }
    );
    return (res as any)?.data ?? res;
  },

  async previewBoqCostingMethod(
    estimateId: string,
    lineId: string,
    body: { revisionId: string; method: string; reason?: string }
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/${lineId}/costing-method/preview`,
      body
    );
    return (res as any)?.data ?? res;
  },

  /**
   * Confirm BOQ method change. FE uses transitionMode REPLACE|PRESERVE;
   * BE expects behavior replace|preserve.
   */
  async confirmBoqCostingMethod(
    estimateId: string,
    lineId: string,
    body: {
      revisionId: string;
      method: string;
      reason?: string;
      transitionMode?: 'REPLACE' | 'PRESERVE';
      behavior?: 'replace' | 'preserve';
    }
  ) {
    const mode = body.transitionMode;
    const behavior =
      body.behavior ||
      (mode === 'PRESERVE' ? 'preserve' : mode === 'REPLACE' ? 'replace' : 'replace');
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/${lineId}/costing-method/confirm`,
      {
        revisionId: body.revisionId,
        method: body.method,
        reason: body.reason,
        behavior,
        transitionMode: mode || (behavior === 'preserve' ? 'PRESERVE' : 'REPLACE'),
      }
    );
    return (res as any)?.data ?? res;
  },

  async previewBoqAssembly(
    estimateId: string,
    lineId: string,
    body: { revisionId: string; assemblyVersionId: string }
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/${lineId}/assemblies/preview`,
      body
    );
    return (res as any)?.data ?? res;
  },

  async applyBoqAssembly(
    estimateId: string,
    lineId: string,
    body: {
      revisionId: string;
      assemblyVersionId: string;
      reason?: string;
      replaceExisting?: boolean;
    }
  ) {
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/client-boq/${lineId}/assemblies/apply`,
      body
    );
    return (res as any)?.data ?? res;
  },

  async fetchRateCandidates(
    estimateId: string,
    body: {
      revisionId?: string;
      resourceKind: string;
      resourceRefId?: string;
      resourceRefType?: string;
      materialId?: string;
      labourClassId?: string;
      equipmentClassId?: string;
      uom?: string;
      currency?: string;
      quantity?: number;
    }
  ) {
    const resourceRefId =
      body.resourceRefId ||
      body.materialId ||
      body.labourClassId ||
      body.equipmentClassId ||
      undefined;
    const res = await apiClient.post(`/epc/estimations/${estimateId}/rate-candidates`, {
      revisionId: body.revisionId,
      resourceKind: body.resourceKind,
      resource_kind: body.resourceKind,
      resourceRefId,
      resource_ref_id: resourceRefId,
      resourceRefType: body.resourceRefType,
      uom: body.uom,
      currency: body.currency,
      quantity: body.quantity,
    });
    return (res as any)?.data ?? res;
  },

  async applyRateCandidate(
    estimateId: string,
    lineId: string,
    body: {
      revisionId?: string;
      candidate: Record<string, unknown>;
      reason?: string;
      overrideReason?: string;
    }
  ) {
    const overrideReason = body.overrideReason || body.reason;
    const res = await apiClient.post(
      `/epc/estimations/${estimateId}/cost-lines/${lineId}/apply-rate`,
      {
        revisionId: body.revisionId,
        candidate: body.candidate,
        overrideReason,
        reason: overrideReason,
      }
    );
    return (res as any)?.data ?? res;
  },
};
